import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ArrowBack, Add, Edit, Delete } from "@mui/icons-material";
import PageLayout from "../components/admin/PageLayout";

const CommitteeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [committee, setCommittee] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    name: "",
    mobile: "",
    email: "",
    address: "",
  });
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  const fetchCommittee = async () => {
    try {
      const [commRes, memRes] = await Promise.all([
        apiClient.get(`committee/${id}`),
        apiClient.get(`committee/${id}/members`),
      ]);
      if (commRes.data.success) setCommittee(commRes.data.data);
      if (memRes.data.success) setMembers(memRes.data.data);
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to load committee", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommittee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const openAddMember = () => {
    setEditingMember(null);
    setMemberForm({ name: "", mobile: "", email: "", address: "" });
    setMemberDialogOpen(true);
  };

  const openEditMember = (m) => {
    setEditingMember(m);
    setMemberForm({
      name: m.name || "",
      mobile: m.mobile || "",
      email: m.email || "",
      address: m.address || "",
    });
    setMemberDialogOpen(true);
  };

  const handleMemberSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editingMember) {
        const res = await apiClient.put(`committee/${id}/members/${editingMember.id}`, memberForm);
        if (res.data.success) {
          showSnackbar("Member updated");
          setMemberDialogOpen(false);
          fetchCommittee();
        } else showSnackbar(res.data.message || "Update failed", "error");
      } else {
        const res = await apiClient.post(`committee/${id}/members`, memberForm);
        if (res.data.success) {
          showSnackbar("Member added");
          setMemberDialogOpen(false);
          fetchCommittee();
        } else showSnackbar(res.data.message || "Add failed", "error");
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Request failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMemberClick = (m) => {
    setMemberToDelete(m);
    setDeleteDialogOpen(true);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;
    try {
      const res = await apiClient.delete(`committee/${id}/members/${memberToDelete.id}`);
      if (res.data.success) {
        showSnackbar("Member removed");
        setMembers((prev) => prev.filter((x) => x.id !== memberToDelete.id));
      } else showSnackbar(res.data.message || "Remove failed", "error");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Request failed", "error");
    }
    setDeleteDialogOpen(false);
    setMemberToDelete(null);
  };

  if (loading) {
    return (
      <PageLayout title="Committee" subtitle="Loading…">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress size={48} sx={{ color: "primary.main" }} />
        </Box>
      </PageLayout>
    );
  }

  if (!committee) {
    return (
      <PageLayout title="Committee" subtitle="Committee not found." action={<Button startIcon={<ArrowBack />} onClick={() => navigate("/committees")}>Back to Committees</Button>}>
        <Typography color="error">Committee not found.</Typography>
      </PageLayout>
    );
  }

  const displayType = (t) => (t === "auction" ? "Auction" : (t && t.charAt(0).toUpperCase() + t.slice(1)) || "");

  return (
    <PageLayout
      title={committee.name}
      subtitle={committee.description || displayType(committee.type)}
      action={
        <Box display="flex" alignItems="center" gap={1}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/committees")} size="small">
            Back
          </Button>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={openAddMember}>
            Add Member
          </Button>
        </Box>
      }
    >
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Address</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No members yet. Add members to this committee.
                </TableCell>
              </TableRow>
            ) : (
              members.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.mobile}</TableCell>
                  <TableCell>{m.email || "–"}</TableCell>
                  <TableCell>{m.address || "–"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEditMember(m)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteMemberClick(m)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Member Dialog */}
      <Dialog open={memberDialogOpen} onClose={() => setMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMember ? "Edit Member" : "Add Member"}</DialogTitle>
        <form onSubmit={handleMemberSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="Name"
                value={memberForm.name}
                onChange={(e) => setMemberForm((p) => ({ ...p, name: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Mobile"
                value={memberForm.mobile}
                onChange={(e) => setMemberForm((p) => ({ ...p, mobile: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={memberForm.email}
                onChange={(e) => setMemberForm((p) => ({ ...p, email: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Address"
                value={memberForm.address}
                onChange={(e) => setMemberForm((p) => ({ ...p, address: e.target.value }))}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMemberDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={24} /> : editingMember ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          Are you sure you want to remove this member from the committee?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteMember} color="error">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default CommitteeDetail;
